import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { Box, InputAdornment, TextField } from '@mui/material';

import { useDebounce } from 'src/hooks';
import type { ActionItemStatus, NpsGroup } from 'src/types/followUp';
import { apiPaths } from 'src/services/api/apiPaths';
import OpenPopupProvider from 'src/components/app/context/OpenPopupContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useOwnerListQuery from 'src/components/myFollowUp/hooks/useOwnerListQuery';
import useFetchSurveyList from 'src/components/survey/SurveyContext/ListContext/useFetchSurveyList';

import { useTicketTableContext } from '../TicketFilterContext';

import FilterSection from './FilterSection';
import FollowUpOwnerSearch from './FollowUpOwnerSearch';

import { styles } from './index.styles';

export const All = 'All';
// empty default array as reference for hooks to prevent rerendering
const DEFAULT_FILTERS: never[] = [];

const npsGroups: Array<{
  value: NpsGroup | typeof All;
  translationKey: string;
}> = [
  {
    value: 'Detractor',
    translationKey: 'npsGroup.detractor',
  },
  { value: 'Promoter', translationKey: 'npsGroup.promoter' },
  {
    value: 'Passive',
    translationKey: 'npsGroup.passive',
  },
];
const ticketStatus: Array<{
  value: ActionItemStatus | typeof All;
  translationKey: string;
}> = [
  { value: 'Open', translationKey: 'status.open' },
  { value: 'Closed', translationKey: 'status.closed' },
  { value: 'In Progress', translationKey: 'status.inProgress' },
  { value: 'Overdue', translationKey: 'status.overdue' },
];

export interface FilterSections {
  isGlobalDashboard?: boolean;
  search?: boolean;
  nps?: boolean;
  status?: boolean;
  owner?: boolean;
  survey?: boolean;
}

const FilterColumn: FC<FilterSections> = ({
  isGlobalDashboard = false,
  search = true,
  nps = true,
  status = true,
  owner = true,
  survey = true,
}) => {
  const { searchString, setSearchString } = useTicketTableContext();
  const { t } = useTranslation('followUp');
  const { user } = useUserContext();
  const [ownerSearchString, setOwnerSearchString] = useState<string>('');
  const debouncedOwnerSearch = useDebounce(ownerSearchString, 500);

  const { data: followUpOwners } = useOwnerListQuery({
    path: isGlobalDashboard
      ? apiPaths.MY_FOLLOW_UP.OWNERS
      : apiPaths.FOLLOW_UP.OWNERS,
    search: debouncedOwnerSearch,
  });

  // NPS filter
  const npsFilterOptions = useMemo(
    () =>
      npsGroups.map(({ value, translationKey }) => ({
        value,
        displayString: t(translationKey),
      })),
    [npsGroups, t],
  );

  // Survey filter
  const { data: surveys } = useFetchSurveyList();
  const surveyFilterOptions = useMemo(
    () =>
      surveys
        ?.slice()
        .sort(({ name: nameA }, { name: nameB }) =>
          nameA < nameB ? -1 : nameA > nameB ? 1 : 0,
        )
        .map(({ id, name }) => ({
          value: String(id),
          displayString: name,
        })) || DEFAULT_FILTERS,
    [surveys],
  );

  // Status filter
  const statusFilterOptions = useMemo(
    () =>
      ticketStatus.map(({ value, translationKey }) => ({
        value,
        displayString: t(translationKey),
      })),
    [ticketStatus, t],
  );

  // Owner filter
  const ownerFilterOptions = useMemo(
    () => [
      {
        value: user ? String(user.id) : '',
        displayString: t('myTickets'),
      },
      ...(followUpOwners?.map(({ id, fullName }) => ({
        value: String(id),
        displayString: fullName,
      })) ?? DEFAULT_FILTERS),
    ],
    [user, t, followUpOwners],
  );

  return (
    <OpenPopupProvider>
      <Box sx={styles.filterColumn}>
        {search && (
          <TextField
            aria-label="Ticket search"
            fullWidth
            placeholder={t('toolBar.searchTickets')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch />
                </InputAdornment>
              ),
              sx: (theme) => ({
                border: '2px solid',
                borderColor: theme.colors.neutral[100],
              }),
            }}
            value={searchString}
            onChange={(event) => setSearchString(event.target.value)}
          />
        )}
        {nps && (
          <FilterSection
            filterKey={t('tableHeader.npsGroup')}
            allFilterOptions={npsFilterOptions}
            popupKey="nps"
          >
            {t('toolBar.npsGroupFilterLabel')}
          </FilterSection>
        )}

        {survey && (
          <FilterSection
            filterKey={'survey'}
            allFilterOptions={surveyFilterOptions}
            popupKey="survey"
          >
            {t('toolBar.surveyFilterLabel')}
          </FilterSection>
        )}

        {status && (
          <FilterSection
            filterKey={t('status.status')}
            allFilterOptions={statusFilterOptions}
            popupKey="status"
          >
            {t('toolBar.ticketStatusFilterLabel')}
          </FilterSection>
        )}

        {owner && (
          <FilterSection
            filterKey={t('tableHeader.followUpOwner')}
            allFilterOptions={ownerFilterOptions}
            popupKey="owner"
            searchBar={
              <FollowUpOwnerSearch
                searchString={ownerSearchString}
                setSearchString={setOwnerSearchString}
              />
            }
          >
            {t('toolBar.followUpOwnerFilterLabel')}
          </FilterSection>
        )}
      </Box>
    </OpenPopupProvider>
  );
};

export default FilterColumn;
